import React, { useEffect, useRef, useState } from 'react';

import GrowPortfolioPage from './pages/grow/GrowPortfolioPage';

const NAV_ITEMS = [
  { key: 'home', label: 'Home', hash: '#/home' },
  { key: 'fund', label: 'Fund', hash: '#/fund' },
  { key: 'grow', label: 'Grow', hash: '#/grow' },
  { key: 'discover', label: 'Discover', hash: '#/discover' }
];

function parseHash() {
  const h = window.location.hash || '#/home';
  const key = h.replace('#/', '');
  const [route, subRoute] = key.split('/');
  return { route: route || 'home', subRoute: subRoute || '' };
}

function normalizeGrowSubRoute(subRoute) {
  if (subRoute === 'portfolio') return 'portfolio';
  if (subRoute === 'ethereum') return 'ethereum';
  if (subRoute === 'arbitrum') return 'arbitrum';
  return 'arbitrum';
}

function seedGlobals() {
  window.WALLET_ASSETS = {
    1: [
      { address: '0xA0b86991c6218b36C1d19D4a2e9Eb0cE3606eB48', symbol: 'USDC' },
      { address: '0xdAC17F958D2ee523a2206206994597C13D831ec7', symbol: 'USDT' },
      { address: '0xae7ab96520DE3A18E5e111B5EaAb095312D7fE84', symbol: 'stETH' },
      { address: '0x7f39c581F595B53c5cb19BD0b3f8Da6c935E2Ca0', symbol: 'wstETH' }
    ],
    42161: [
      { address: '0xaf88d065e77c8C C2239327C5EDb3A432268e5831'.replace(' ', ''), symbol: 'USDC' },
      { address: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9', symbol: 'USDT' },
      { address: '0x5979D7b546E38E414F7E9822514be443A4800529', symbol: 'wstETH' }
    ]
  };

  window.CONFIG = window.CONFIG || {};
  window.CONFIG.BUILD_ID = window.CONFIG.BUILD_ID || String(Date.now());
  window.CONFIG.AAVE = window.CONFIG.AAVE || {};
  window.CONFIG.AAVE.ATOKENS = {
    1: {
      aWETH: '0x3a3A65aAb0dd2A17E3F1947bA16138cd37d08c04',
      aUSDC: '0xBcca60bB61934080951369a648Fb03DF4F96263C',
      aUSDT: '0x3Ed3B47Dd13EC9a98b44e6204A523E766B225811'
    }
  };
}

function loadScript(src, options = {}) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[data-ts-src="${src}"]`);
    if (existing) {
      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.defer = true;
    script.dataset.tsSrc = src;
    if (options.type) script.type = options.type;
    script.onload = () => resolve();
    script.onerror = (e) => reject(e);
    document.body.appendChild(script);
  });
}

function waitForPartials(timeoutMs = 8000) {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    document.addEventListener('partials:loaded', finish, { once: true });
    setTimeout(finish, timeoutMs);
  });
}

const CHAIN_CONFIG = {
  ethereum: {
    chainId: '0x1',
    chainName: 'Ethereum Mainnet',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: ['https://rpc.ankr.com/eth'],
    blockExplorerUrls: ['https://etherscan.io']
  },
  arbitrum: {
    chainId: '0xa4b1',
    chainName: 'Arbitrum One',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: ['https://rpc.ankr.com/arbitrum'],
    blockExplorerUrls: ['https://arbiscan.io']
  }
};

export default function App() {
  const initialRoute = parseHash();
  const [route, setRoute] = useState(initialRoute.route);
  const [growSubTab, setGrowSubTab] = useState(
    initialRoute.route === 'grow'
      ? normalizeGrowSubRoute(initialRoute.subRoute)
      : 'arbitrum'
  );
  const scriptsLoadedRef = useRef(false);
  const lidoTriggerRef = useRef(null);
  const lastChainRequestRef = useRef({ chainId: null, ts: 0 });

  useEffect(() => {
    window.__TS_APP_SHELL__ = true;
  }, []);

  useEffect(() => {
    if (!window.location.hash) {
      window.location.hash = '#/home';
    }
    const onHash = () => {
      const next = parseHash();
      setRoute(next.route);
      if (next.route === 'grow') {
        setGrowSubTab(normalizeGrowSubRoute(next.subRoute));
      }
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (String(e?.message || '').includes('SES_') || String(e?.filename || '').includes('lockdown-install.js')) {
        const el = document.getElementById('envWarning');
        if (el) {
          el.style.display = 'block';
          el.textContent = 'A browser extension is enforcing SES lockdown and broke scripts. Disable that extension for this site and reload.';
        }
      }
    };
    window.addEventListener('error', handler);
    return () => window.removeEventListener('error', handler);
  }, []);

  useEffect(() => {
    const ENDPOINT = 'https://api.impactgena2.workers.dev/health';
    const setBadge = (text) => {
      const el = document.getElementById('apiHealth');
      if (el) el.textContent = ' · ' + text;
    };
    async function refreshHealth() {
      try {
        const t0 = performance?.now?.() ?? Date.now();
        const res = await fetch(ENDPOINT + '?t=' + Date.now(), { method: 'GET', cache: 'no-store' });
        const dt = (performance?.now?.() ?? Date.now()) - t0;
        const j = await res.json().catch(() => null);
        if (!res.ok || !j || j.ok === false) {
          setBadge('API: degraded');
          return;
        }
        const when = j.now_utc ? new Date(j.now_utc).toLocaleString() : '';
        const svc = j.service || 'api';
        const ver = j.version ? (' v' + j.version) : '';
        setBadge(`API: ${svc}${ver}${when ? ' • ' + when : ''} • ${Math.round(dt)}ms`);
      } catch {
        setBadge('API: unreachable');
      }
    }
    refreshHealth();
    const id = setInterval(refreshHealth, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (scriptsLoadedRef.current) return;
    scriptsLoadedRef.current = true;

    async function boot() {
      seedGlobals();

      await loadScript('/js/config.js');
      await loadScript('/js/price.js');
      await loadScript('/js/app.js');

      const partialsReady = waitForPartials();
      await loadScript('/js/includes.js');
      await partialsReady;

      const dePanelize = (wrapId, selector) => {
        const wrap = document.getElementById(wrapId);
        if (!wrap) return;
        const panel = wrap.querySelector(selector);
        if (!panel) return;
        panel.removeAttribute('id');
        panel.classList.remove('tab-panel');
        panel.removeAttribute('role');
        panel.removeAttribute('aria-labelledby');
        panel.removeAttribute('hidden');
      };
      const fixPanels = () => {
        dePanelize('about-inline-wrap', '#tab-about, .tab-panel');
        dePanelize('wallet-inline-wrap', '#tab-wallet, .tab-panel');
      };
      document.addEventListener('partials:loaded', fixPanels, { once: true });
      fixPanels();

      await loadScript('/js/bridge.js');
      await loadScript('/js/sensei-yield/main.js', { type: 'module' });
      await loadScript('/js/sensei-yield/sleeves-usdc.js', { type: 'module' });
      await loadScript('/js/sensei-yield/admin-usdc.js', { type: 'module' });
      await loadScript('/js/sensei-yield/bluebelt.js', { type: 'module' });
      await loadScript('/js/sensei-yield/whitebelt.js', { type: 'module' });
      await loadScript('/js/sensei-yield/greenbelt.js', { type: 'module' });
      await loadScript('/js/sensei-yield/blackbelt.js', { type: 'module' });
      if (typeof window.__sensei_bb_activate === 'function') {
        window.__sensei_bb_activate();
      }

      await loadScript('/js/defi-scan.js');
      await loadScript('/js/advisor.js');
      await loadScript('/js/aave.js');
      await loadScript('/js/curve.js');
      await loadScript('/js/yearn.js');
      await loadScript('/js/sensei-pool-router.js');
      await loadScript('/js/balances.js');
      await loadScript('/js/env-guard.js');
      await loadScript('/js/walletconnect.js');
      await loadScript('/js/lido.js');
      await loadScript('/js/index-html.js');

      if (window.lucide && typeof window.lucide.createIcons === 'function') {
        window.lucide.createIcons();
      }

      if (typeof window.updateWalletUI === 'function') {
        window.updateWalletUI(window.currentAddressFull, window.currentChainId);
      }
    }

    boot();
  }, []);

  useEffect(() => {
    if (route === 'grow' && growSubTab === 'ethereum' && lidoTriggerRef.current) {
      lidoTriggerRef.current.click();
    }
  }, [route, growSubTab]);

  const setGrowRoute = (tab) => {
    const next = normalizeGrowSubRoute(tab);
    setGrowSubTab(next);
    window.location.hash = `#/grow/${next}`;
  };

  useEffect(() => {
    async function requestChainSwitch() {
      if (route !== 'grow') return;
      if (!window.ethereum || !window.ethereum.request) return;

      const cfg = CHAIN_CONFIG[growSubTab];
      if (!cfg) return;

      const now = Date.now();
      if (lastChainRequestRef.current.chainId === cfg.chainId && (now - lastChainRequestRef.current.ts) < 15000) {
        return;
      }

      lastChainRequestRef.current = { chainId: cfg.chainId, ts: now };

      const syncAfterSwitch = () => {
        if (typeof window.updateWalletUI === 'function') {
          window.updateWalletUI(window.currentAddressFull, cfg.chainId);
        } else if (typeof window.refreshGrowWalletSnapshot === 'function') {
          const n = parseInt(cfg.chainId, 16);
          window.refreshGrowWalletSnapshot(window.currentAddressFull, Number.isFinite(n) ? n : undefined);
        }
      };

      try {
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: cfg.chainId }]
        });
        setTimeout(syncAfterSwitch, 300);
        setTimeout(syncAfterSwitch, 1500);
      } catch (err) {
        if (err && err.code === 4902) {
          try {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [cfg]
            });
            setTimeout(syncAfterSwitch, 300);
            setTimeout(syncAfterSwitch, 1500);
          } catch (_) {}
        }
      }
    }

    requestChainSwitch();
  }, [route, growSubTab]);

  return (
    <div className="ts-app">
      <div id="ts-splash" aria-live="polite" aria-busy="true" hidden>
        <div className="ts-splash-mark">
          <img src="/assets/tradersensei-icon.png" alt="TraderSensei" width="128" height="128" />
        </div>
        <p className="ts-splash-text">Loading TraderSensei…</p>
      </div>

      <div id="envWarning" className="muted small" style={{ display: 'none', color: '#b00', margin: '.5rem 0' }}></div>

      <header className="ts-header" id="tsHeader">
        <div className="ts-header-inner">
          <div className="ts-brand">
            <a href="#/home" className="ts-brand-link">
              <img className="ts-brand-logo" src="/assets/tradersensei-icon.png" alt="TraderSensei" />
            </a>
            <span className="ts-brand-link">TraderSensei powered by</span>
            <a
              href="https://genaimpact.com"
              className="ts-brand-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              GenaImpact
            </a>
          </div>
          <div className="ts-header-right">
            <div id="walletMount" className="ts-wallet">
              <div className="ts-wallet-summary">
                <span className="muted">Wallet</span>
                <span id="headerAccountDisplay" className="mono">—</span>
                <span id="headerChainDisplay" className="muted">—</span>
              </div>
              <button className="btn" id="connectBtn">Connect Wallet</button>
            </div>
          </div>
        </div>
      </header>

      <div className="ts-shell">
        <aside className="ts-sidebar" id="tsSidebar">
          {NAV_ITEMS.map(item => (
            <a
              key={item.key}
              className={`ts-nav-item${route === item.key ? ' active' : ''}`}
              data-route={item.key}
              href={item.hash}
            >
              {item.label}
            </a>
          ))}
        </aside>

        <main className="ts-content" id="tsContent">
          <div className="ts-content-inner" id="tsContentInner">
            <section id="panel-home" hidden={route !== 'home'}>
              <div className="ts-card">
                <h2 style={{ margin: '0 0 8px 0' }}>Home</h2>
                <p style={{ margin: '0 0 12px 0', opacity: 0.85 }}>
                  Quick actions and your wallet status.
                </p>
                <div className="ts-home-actions" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <a className="ts-nav-item" data-route="fund" href="#/fund">Add / Bridge USDC</a>
                  <a className="ts-nav-item" data-route="grow" href="#/grow">Deposit to Strategy</a>
                  <a className="ts-nav-item" data-route="discover" href="#/discover">Scan Rates</a>
                </div>
              </div>

              <section className="card" style={{ marginTop: '1rem' }}>
                <details id="homeWelcomeDetails" open>
                  <summary style={{ cursor: 'pointer' }}><h3 style={{ display: 'inline' }}>Welcome</h3></summary>
                  <div style={{ marginTop: '.75rem' }}>
                    <div data-include="/partials/welcome.html"></div>
                    <div id="about-inline-wrap" style={{ marginTop: '1rem' }}>
                      <div id="about-inline" data-include="/partials/about.html"></div>
                    </div>
                  </div>
                </details>
              </section>

              <section id="wallet-collapsible" className="card" style={{ marginTop: '1rem' }}>
                <details open>
                  <summary style={{ cursor: 'pointer' }}><h3 style={{ display: 'inline' }}>Wallet</h3></summary>
                  <div id="wallet-inline-wrap" style={{ marginTop: '.75rem' }}>
                    <div id="wallet-inline" data-include="/partials/wallet.html"></div>
                  </div>
                </details>
              </section>
            </section>

            <section id="panel-fund" hidden={route !== 'fund'}>
              <div data-include="/partials/bridge.html"></div>
            </section>

            <section id="panel-grow" hidden={route !== 'grow'}>
              <div className="ts-card ts-sticky-card" id="growWalletCard">
                <div className="ts-grow-wallet">
                  <div>
                    <div className="muted small">Wallet</div>
                    <div id="growWalletAccount" className="mono">—</div>
                    <div className="ts-grow-chain-line">
                      <span id="growWalletChain" className="muted small">—</span>
                      <span id="growWalletChainBadge" className="ts-chain-badge">—</span>
                    </div>
                  </div>
                  <div>
                    <div className="muted small">USDC (this network)</div>
                    <div id="growWalletUsdc" className="mono">—</div>
                    <div id="growWalletNative" className="muted small">—</div>
                  </div>
                  <div className="ts-grow-wallet-actions">
                    <button id="growWalletRefresh" className="btn btn-outline" type="button">Refresh</button>
                  </div>
                </div>
                <div id="growWalletStatus" className="tiny muted" style={{ marginTop: '.5rem' }}>
                  Connect wallet to see balances.
                </div>
                <div id="growWalletAssets" className="asset-list" style={{ marginTop: '.6rem' }}>
                  —
                </div>
              </div>

              <nav className="tabs" aria-label="Grow networks">
                <button
                  className={`tab${growSubTab === 'ethereum' ? ' active' : ''}`}
                  type="button"
                  data-tab="lido"
                  onClick={() => setGrowRoute('ethereum')}
                >
                  Ethereum
                </button>
                <button
                  className={`tab${growSubTab === 'arbitrum' ? ' active' : ''}`}
                  type="button"
                  onClick={() => setGrowRoute('arbitrum')}
                >
                  Arbitrum
                </button>
                <button
                  className={`tab${growSubTab === 'portfolio' ? ' active' : ''}`}
                  type="button"
                  onClick={() => setGrowRoute('portfolio')}
                >
                  Portfolio
                </button>
              </nav>

              <section
                id="tab-lido"
                className={`tab-panel${route === 'grow' && growSubTab === 'ethereum' ? ' active' : ''}`}
                hidden={growSubTab !== 'ethereum'}
              >
                <div className="card" style={{ marginTop: '0.5rem' }}>
                  <h3 style={{ margin: '0 0 6px 0' }}>Sensei Staking on Ethereum</h3>
                  <div className="muted small">ETH staking via Lido, with instant liquidity options.</div>
                </div>
                <div data-include="/partials/lido.html"></div>
                <div data-include="/partials/aave.html"></div>
              </section>

            <section
              id="grow-arbitrum"
              className={`tab-panel${route === 'grow' && growSubTab === 'arbitrum' ? ' active' : ''}`}
              hidden={growSubTab !== 'arbitrum'}
            >
              <div data-include="/partials/whitebelt.html"></div>
              <div data-include="/partials/greenbelt.html"></div>
              <div data-include="/partials/bluebelt.html"></div>
              <div data-include="/partials/blackbelt.html"></div>
            </section>

            <section
              id="grow-portfolio"
              className={`tab-panel${route === 'grow' && growSubTab === 'portfolio' ? ' active' : ''}`}
              hidden={growSubTab !== 'portfolio'}
            >
              <GrowPortfolioPage />
            </section>
          </section>

            <section id="tab-defi" className={`tab-panel${route === 'discover' ? ' active' : ''}`} hidden={route !== 'discover'}>
              <div data-include="/partials/defi-scan.html"></div>
            </section>

            <footer className="site-footer ts-footer" id="tsFooter">
              <small>
                © <span id="year"></span> TraderSensei
                <span id="buildIdDisplay" className="muted"></span>
                <span id="apiHealth" className="muted"> · API: checking…</span>
                <br />
                <span className="muted">Powered by GNIM, the GenaImpact ecosystem token.</span>
              </small>
            </footer>
          </div>
        </main>
      </div>

      <nav className="ts-bottomnav" id="tsBottomNav">
        {NAV_ITEMS.map(item => (
          <a
            key={item.key}
            className={`ts-nav-item${route === item.key ? ' active' : ''}`}
            data-route={item.key}
            href={item.hash}
          >
            {item.label}
          </a>
        ))}
      </nav>

      <button
        ref={lidoTriggerRef}
        data-tab="lido"
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        style={{ display: 'none' }}
      >
        Lido Trigger
      </button>
    </div>
  );
}
